import {
    BadgeIndianRupee,
    ShieldCheck,
} from "lucide-react";
import {
    CheckoutPricing,
} from "../../CheckoutPricingTypes";
import "./CheckoutPriceDetails.css";

type CheckoutPriceDetailsProps = {
    pricing: CheckoutPricing;
};

const formatPrice = (
    value: number
) =>
    new Intl.NumberFormat(
        "en-IN"
    ).format(
        Math.max(
            0,
            Math.round(
                value
            )
        )
    );

const CheckoutPriceDetails = ({
    pricing,
}: CheckoutPriceDetailsProps) => {
    const {
        totalItems,
        itemsTotal,
        shoppingVoucher = 0,
        salarCoins = 0,
        salarCodeDiscount = 0,
        couponDiscount = 0,
        couponCode,
        salarCode,
        totalSavings = 0,
        isInfluencerCoupon = false,
        deliveryFee,
        platformFee,
        orderTotal,
    } = pricing;

    const hasAnyDiscount =
        shoppingVoucher > 0 ||
        salarCoins > 0 ||
        couponDiscount > 0 ||
        salarCodeDiscount > 0;

    return (
        <aside className="checkout-price-details">
            <div className="checkout-price-card">
                <div className="checkout-price-heading">
                    <div>
                        <span>
                            YOUR TOTAL
                        </span>

                        <h2>
                            Price details
                        </h2>
                    </div>

                    <div className="checkout-price-heading-icon">
                        <BadgeIndianRupee
                            size={17}
                            strokeWidth={1.9}
                        />
                    </div>
                </div>

                <div className="checkout-price-body">
                    <div className="checkout-price-row checkout-price-items">
                        <span>
                            Total Items (
                            {
                                totalItems
                            }
                            )
                        </span>

                        <strong>
                            ₹{" "}
                            {formatPrice(
                                itemsTotal
                            )}
                        </strong>
                    </div>

                    {hasAnyDiscount && <div className="checkout-price-divider" />}

                    {shoppingVoucher > 0 && (
                        <div className="checkout-price-row checkout-price-discount">
                            <span>
                                Shopping Voucher
                            </span>

                            <strong>
                                - ₹
                                {formatPrice(
                                    shoppingVoucher
                                )}
                            </strong>
                        </div>
                    )}

                    {couponDiscount > 0 && (
                        <div className="checkout-price-row checkout-price-discount">
                            <span>
                                {isInfluencerCoupon ? "Influencer Coupon" : "Coupon Discount"}
                                {couponCode && (
                                    <small style={{ marginLeft: "4px", color: "#228b45" }}>
                                        ({couponCode})
                                    </small>
                                )}
                            </span>

                            <strong>
                                - ₹
                                {formatPrice(
                                    couponDiscount
                                )}
                            </strong>
                        </div>
                    )}

                    {salarCoins > 0 && (
                        <div className="checkout-price-row checkout-price-discount">
                            <span>
                                Salar Coins
                            </span>

                            <strong>
                                - ₹
                                {formatPrice(
                                    salarCoins
                                )}
                            </strong>
                        </div>
                    )}

                    {salarCodeDiscount > 0 && (
                        <div className="checkout-price-row checkout-price-discount checkout-price-code">
                            <span>
                                Salar Code Applied{" "}
                                {salarCode && (
                                    <small>
                                        ({salarCode})
                                    </small>
                                )}
                            </span>

                            <strong>
                                - ₹
                                {formatPrice(
                                    salarCodeDiscount
                                )}
                            </strong>
                        </div>
                    )}

                    {hasAnyDiscount && (
                        <p className="checkout-price-note">
                            Enjoy Discounted Offers!
                        </p>
                    )}

                    <div className="checkout-price-divider" />

                    <div className="checkout-price-fee-block">
                        <div className="checkout-price-row">
                            <span>
                                Delivery Fee
                            </span>

                            <strong>
                                {deliveryFee === 0 ? "FREE" : `₹ ${formatPrice(deliveryFee)}`}
                            </strong>
                        </div>

                        <p className="checkout-price-note">
                            Incl. all taxes & charges
                        </p>
                    </div>

                    <div className="checkout-price-divider checkout-price-divider-light" />

                    <div className="checkout-price-fee-block">
                        <div className="checkout-price-row">
                            <span>
                                Platform Fee
                            </span>

                            <strong>
                                ₹
                                {formatPrice(
                                    platformFee
                                )}
                            </strong>
                        </div>

                        <p className="checkout-price-note">
                            Incl. all taxes & charges
                        </p>
                    </div>

                    <div className="checkout-price-total">
                        <span>
                            Order Total
                        </span>

                        <strong>
                            ₹
                            {formatPrice(
                                orderTotal
                            )}
                        </strong>
                    </div>

                    {totalSavings > 0 && (
                        <div className="checkout-price-savings">
                            You are saving ₹{formatPrice(totalSavings)} on this order!
                        </div>
                    )}
                </div>

                <div className="checkout-price-secure">
                    <ShieldCheck
                        size={13}
                        strokeWidth={2}
                    />

                    <span>
                        Secure pricing by Salar
                    </span>
                </div>
            </div>
        </aside>
    );
};

export default CheckoutPriceDetails;
